import { useAfterFirstRender } from "@/app/hooks/useAfterFirstRender";
import { useArrayConstants } from "@/app/hooks/useArrayConstants";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { VMS_CLICK_EVENTS, VMS_SCREEN_IDS, useVMSClickLog } from "@/app/hooks/useVMSClickLog";
import { useAppDispatch } from "@/app_redux/hooks/root_hook";
import { setLoading } from "@/app_redux/reducers/loading_reducer";
import SimpleCheckbox from "@/app/components/ui/CheckBoxes/SimpleCheckbox";
import InputWithEndIcon from "@/app/components/ui/Inputs/InputWithEndIcon";

import {
  ICustomerProfileState,
  setCustomerPets,
  setCustomerSearchQuery,
  setCustomerSearchQueryToggle,
} from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useState } from "react";
import { IInputSearchCustomerProps } from "../../customer_profile_interface";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useForm } from "react-hook-form";
import RBACFlag from "@/app/components/core/RBACFlag";
import { useClinicListAPI } from "@/app/hooks/api_hooks/org/useClinicListAPI";

const InputSearchCustomer: React.FC<IInputSearchCustomerProps> = ({ searchCustomer }) => {
  // Instantiations //
  const [toggle, setToggle] = useState<"1" | "0">("0");
  const [query, setQuery] = useState<string>("");
  const { getRouteKey } = useRouter();
  const { customerProfileSearchToggles } = useArrayConstants();
  const dispatch = useAppDispatch();
  const { logVMSClick } = useVMSClickLog(VMS_SCREEN_IDS.SEARCH_CUSTOMER);

  const { routeState } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id"));
  const { searchCustomerToggleCode, searchCustomerQuery } = routeState?.state as ICustomerProfileState;

  // Instantiations Ends  //

  const formHook = useForm<any>({
    mode: "onChange",
    defaultValues: {
      clinic_id: null,
    },
  });

  const { execute: fetchClinicListAPI,clinicList } = useClinicListAPI();

  useEffect(() => {
    fetchClinicListAPI();
  }, []);


  useEffect(() => {
    if (searchCustomerQuery?.length > 3) {
      setQuery(searchCustomerQuery);
    }
  }, [searchCustomerQuery]);

  useAfterFirstRender(() => {
    setToggle(searchCustomerToggleCode);
  }, [searchCustomerToggleCode]);

  useEffect(() => {
    setToggle(searchCustomerToggleCode);
  }, []);

  const handleSearch = (ctaText: string) => {
    const trimmedQuery = query.trim();
    if (trimmedQuery !== "") {
      logVMSClick(VMS_CLICK_EVENTS.SEARCH_CUSTOMER_SUBMIT, ctaText, {
        search_item: trimmedQuery,
        search_tag: customerProfileSearchToggles.find((t) => t.id === toggle)?.title,
        filter_values: { clinic_id: formHook.watch("clinic_id") },
      });
      dispatch(
        setLoading({
          key: "isCustomerSearchInProgress",
          status: true,
        }),
      );
      dispatch(setCustomerSearchQuery(trimmedQuery));
      dispatch(setCustomerSearchQueryToggle(toggle));
      searchCustomer(trimmedQuery, toggle, formHook.watch("clinic_id"));
    }
  };

  const handleChange = (e: any) => {
    setQuery(e.target.value);
    setCustomerPets({ pets: null });
  };

  const clinicsOrgItem = useSelectMenuReducer(clinicList, "clinic_name", "id");

  return (
    <div className="">
      <div className="flex justify-start items-center w-full gap-2 sm:gap-3">
        <InputWithEndIcon
          value={query}
          onChange={handleChange}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleSearch("Enter");
            }
          }}
          containerClassName="flex-1 min-w-0"
          placeholder="Search with name, number and id"
          autoFocus
          icon={<MagnifyingGlassIcon className="w-4 h-4 md:w-5 md:h-5" />}
          handelBack={() => handleSearch("Search")}
        />
        <RBACFlag actionId="CLINIC_WISE_CUSTOMER_SEARCH">
          {" "}
          <>
            <div className="hidden sm:block">
              <SearchableSelectMenu
                errorMessages={[]}
                onSelectItem={(item) => {
                  if (item) {
                    logVMSClick(VMS_CLICK_EVENTS.SEARCH_CUSTOMER_CLINIC_SELECT, item.title, {
                      filter_values: { clinic_id: item.id, clinic_name: item.title },
                    });
                    formHook.setValue("clinic_id", item.id);
                  }
                }}
                fieldError={formHook.formState.errors.clinic_id}
                register={formHook.register("clinic_id", { required: false })}
                selectItems={clinicsOrgItem}
                placeholder="Select Clinic"
                containerClassName="w-72"
                itemsClassName="w-full"
                className="w-full"
                showTooltips={false}
                showTypedErrors={false}
                showDropdownIcon={true}
              />
            </div>
          </>
        </RBACFlag>
        <div className="flex justify-start items-center shrink-0">
          <button
            type="button"
            onClick={() => handleSearch("Search")}
            className="border px-4 sm:px-6 py-2 rounded-lg bg-black text-white border-transparent text-[11px] whitespace-nowrap"
          >
            Search
          </button>
        </div>
      </div>

      <div className="w-full mt-3 md:mt-2">
        <div className="w-full">
          <fieldset className="">
            <div className=" gap-x-3 flex justify-start items-center sm:space-x-10 sm:space-y-0">
              {customerProfileSearchToggles.map((t) => (
                <div key={t.id} className="flex items-center">
                  <SimpleCheckbox
                    onChange={() => {
                      logVMSClick(VMS_CLICK_EVENTS.SEARCH_CUSTOMER_TYPE_CHANGE, `Search in ${t.title}`);
                      setToggle(t.id as "0" | "1");
                    }}
                    checked={toggle === t.id}
                    id={t.id}
                    colorClassName="text-blue-500"
                    descriptionContent={
                      <label htmlFor={t.id} className="ml-3 block text-xs leading-6 text-gray-700">
                        Search in {t.title}
                      </label>
                    }
                  />
                </div>
              ))}
            </div>
          </fieldset>
        </div>
      </div>
    </div>
  );
};

export default InputSearchCustomer;
